import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { SUBJECTS } from '@jeraisonne/db';
import type { SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import { Button, Alert, AlertDescription } from '@jeraisonne/ui';
import { supabase } from '@/lib/supabase';
import { SubjectsMultiselect } from '@/components/subjects-multiselect';

interface StudentSubjectsControlProps {
  userId: string;
  currentLevel: SchoolLevel | null;
  currentSubjects: SubjectSlug[];
}

/**
 * Édition libre-service des matières de l'élève (migration 0226) — remplace
 * l'ancien affichage lecture-seule qui renvoyait vers « contacte ton
 * professeur » sans qu'aucune UI tuteur n'existe pour ça. Contrairement au
 * niveau (`StudentLevelControl`), pas de cooldown : les matières sont un
 * choix librement révisable, RLS `own_update` déjà en place sur la même
 * table. La confirmation pose aussi `subjects_reviewed_at` (même sans
 * changement) pour fermer le rappel de rentrée (`@/lib/subjects-review`).
 */
export function StudentSubjectsControl({
  userId,
  currentLevel,
  currentSubjects,
}: StudentSubjectsControlProps) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<SubjectSlug[]>(currentSubjects);

  const changeMutation = useMutation({
    mutationFn: async (subjects: SubjectSlug[]) => {
      // `subjects_reviewed_at` (migration 0226) est postérieur aux types
      // générés (stale, cf. CLAUDE.md) → client générique.
      const { error } = await (supabase as unknown as SupabaseClient)
        .from('student_profiles')
        .update({ subjects, subjects_reviewed_at: new Date().toISOString() })
        .eq('id', userId);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['student-profile', userId] });
      setEditing(false);
    },
  });

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <span className="text-ink-600 text-sm">Matières</span>
        {!editing && (
          <div className="flex flex-wrap justify-end gap-1.5">
            {currentSubjects.length === 0 ? (
              <span className="text-ink-400 text-sm">—</span>
            ) : (
              currentSubjects.map((s) => (
                <span
                  key={s}
                  className="bg-primary-100 text-primary-700 rounded-full px-2 py-0.5 text-xs font-medium"
                >
                  {SUBJECTS.find((sub) => sub.value === s)?.label ?? s}
                </span>
              ))
            )}
          </div>
        )}
      </div>

      {!editing && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            setSelected(currentSubjects);
            setEditing(true);
          }}
        >
          Modifier
        </Button>
      )}

      {editing && (
        <div className="border-ink-100 space-y-3 rounded-xl border bg-white p-3">
          <SubjectsMultiselect value={selected} onChange={setSelected} levelFilter={currentLevel} />

          {changeMutation.isError && (
            <Alert variant="error">
              <AlertDescription>
                {(changeMutation.error as Error)?.message ?? 'Une erreur est survenue.'}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full sm:w-auto"
              onClick={() => {
                setEditing(false);
                changeMutation.reset();
              }}
              disabled={changeMutation.isPending}
            >
              Annuler
            </Button>
            <Button
              type="button"
              size="sm"
              className="w-full sm:w-auto"
              disabled={changeMutation.isPending}
              onClick={() => changeMutation.mutate(selected)}
            >
              {changeMutation.isPending ? 'Enregistrement…' : 'Confirmer'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
